/**
 * Drill Mix — always-challenge timed drill combining multiple exercise types.
 *
 * Routes: /exercise/mix/:mixId
 * Four mixes: harmony | modes-deep | int-cad | everything
 */

import {
  useReducer,
  useRef,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { Link, useParams } from "react-router-dom";
import {
  buildIntervalExerciseRound,
  buildChordQualityExerciseRound,
  buildChordInversionExerciseRound,
  buildCadenceExerciseRound,
  buildModeExerciseRound,
  buildProgressionExerciseRound,
  intervalDrillItemId,
  chordDrillItemId,
  inversionDrillItemId,
  cadenceDrillItemId,
  modeDrillItemId,
  progressionDrillItemId,
  INTERVAL_POOL,
  CADENCE_TYPE_POOL,
  CADENCE_TYPES_ORDERED,
  INVERSION_CHORD_POOL,
  MODE_DRILL_POOL,
  PROGRESSION_POOL,
  CADENCE_TEMPO_OPTS,
  type IntervalSymbol,
  type InversionIndex,
  type ProgressionKey,
} from "@music-app/exercise-generator";
import { createAudioPlayer, type AudioPlayer } from "@music-app/audio-engine-web";
import type { AudioPlan } from "@music-app/exercise-generator";
import {
  useCardsStore,
  usePrefsStore,
  getChallengeData,
  recordChallengeAttempt,
  recordReview as dbRecordReview,
} from "@music-app/persistence";
import type { Rating } from "@music-app/learning-system";
import { ChallengeTimer } from "../components/ChallengeTimer.js";
import { ChallengeScoreCard } from "../components/ChallengeScoreCard.js";
import { useChallengeTimer } from "../lib/use-challenge-timer.js";
import { nextRoundSeed } from "../lib/round-seed.js";

// ─── Mix definitions ──────────────────────────────────────────────────────────

type MixId = "harmony" | "modes-deep" | "int-cad" | "everything";

interface MixDef {
  title: string;
  subtitle: string;
  challengeKey: string;
  /** Drill type weights (proportional to how often each type appears). */
  types: DrillType[];
}

type DrillType =
  | "interval"
  | "chord"
  | "inversion"
  | "cadence"
  | "mode"
  | "progression";

const MIX_DEFS: Record<MixId, MixDef> = {
  harmony: {
    title: "Harmony Mix",
    subtitle: "Chords · Inversions · Progressions · Cadences",
    challengeKey: "mix-harmony",
    types: ["chord", "inversion", "progression", "cadence"],
  },
  "modes-deep": {
    title: "Modes Deep Mix",
    subtitle: "All seven modes in context",
    challengeKey: "mix-modes-deep",
    types: ["mode", "mode", "mode", "interval"],
  },
  "int-cad": {
    title: "Interval & Cadence Mix",
    subtitle: "Ear training fundamentals",
    challengeKey: "mix-int-cad",
    types: ["interval", "interval", "cadence", "cadence"],
  },
  everything: {
    title: "Everything Mix",
    subtitle: "All drill types",
    challengeKey: "mix-everything",
    types: ["interval", "chord", "inversion", "cadence", "mode", "progression"],
  },
};

// ─── MixItem type ─────────────────────────────────────────────────────────────

interface MixOption {
  id: string;
  label: string;
  sublabel?: string;
}

interface MixItem {
  drillType: DrillType;
  audioPlan: AudioPlan;
  teachAudio?: AudioPlan;
  options: MixOption[];
  correctId: string;
  typeLabel: string;
  itemId: string;
}

// ─── Fixed option pools ────────────────────────────────────────────────────────

const INTERVAL_OPTIONS: MixOption[] = (Object.keys(INTERVAL_POOL) as IntervalSymbol[]).map(
  (sym) => ({ id: sym, label: INTERVAL_POOL[sym].longName }),
);

const CADENCE_OPTIONS: MixOption[] = CADENCE_TYPES_ORDERED.map(
  (ct) => ({ id: ct, label: CADENCE_TYPE_POOL[ct].shortLabel }),
);

const INVERSION_LABELS: Record<InversionIndex, string> = {
  0: "Root",
  1: "1st",
  2: "2nd",
  3: "3rd",
};

const CHORD_QUALITY_OPTIONS: MixOption[] = [
  { id: "maj",    label: "Major",         sublabel: "maj" },
  { id: "min",    label: "Minor",         sublabel: "min" },
  { id: "dim",    label: "Diminished",    sublabel: "dim" },
  { id: "aug",    label: "Augmented",     sublabel: "aug" },
  { id: "maj7",   label: "Major 7th",     sublabel: "maj7" },
  { id: "min7",   label: "Minor 7th",     sublabel: "min7" },
  { id: "dom7",   label: "Dominant 7th",  sublabel: "dom7" },
];

const MODE_OPTIONS: MixOption[] = (
  Object.keys(MODE_DRILL_POOL) as Array<keyof typeof MODE_DRILL_POOL>
).map((m) => ({ id: m, label: MODE_DRILL_POOL[m].displayName }));

const PROGRESSION_OPTIONS: MixOption[] = (
  Object.keys(PROGRESSION_POOL) as ProgressionKey[]
).map((pk) => ({ id: pk, label: PROGRESSION_POOL[pk].displayLabel }));

// ─── Item generator ───────────────────────────────────────────────────────────

function buildMixItem(
  type: DrillType,
  seed: number,
  cadenceTempoKey: keyof typeof CADENCE_TEMPO_OPTS,
): MixItem {
  const cadenceOpts = CADENCE_TEMPO_OPTS[cadenceTempoKey];

  switch (type) {
    case "interval": {
      const round = buildIntervalExerciseRound(
        {
          intervals: ["m2", "M2", "m3", "M3", "P4", "TT", "P5", "m6", "M6", "m7", "M7", "P8"],
          direction: "ascending",
          keyContext: "abstract",
          tonic: "random",
          fixedTonicMidi: 60,
          bottomNote: "tonic",
          drillMode: "graded",
          fsrsEnabled: false,
        },
        seed,
        cadenceOpts,
      );
      return {
        drillType: "interval",
        audioPlan: round.audioPlan,
        teachAudio: round.teachAudio,
        options: INTERVAL_OPTIONS,
        correctId: round.symbol,
        typeLabel: "Interval",
        itemId: intervalDrillItemId(round.symbol, round.effectiveDirection, "abstract"),
      };
    }

    case "chord": {
      const round = buildChordQualityExerciseRound(
        {
          qualities: ["maj", "min", "dim", "aug", "maj7", "min7", "dom7"],
          voicing: "mixed",
          inversion: "root-position",
          drillMode: "graded",
          fsrsEnabled: false,
        },
        seed,
      );
      return {
        drillType: "chord",
        audioPlan: round.audioPlan,
        teachAudio: round.teachAudio,
        options: CHORD_QUALITY_OPTIONS,
        correctId: round.quality,
        typeLabel: "Chord Quality",
        itemId: chordDrillItemId(round.quality, "root-position"),
      };
    }

    case "inversion": {
      const chord = buildChordInversionExerciseRound(
        {
          chordTypes: ["maj", "min", "maj7", "min7", "dom7"],
          inversions: [0, 1, 2, 3],
          voicing: "arpeggiated",
          tonic: "random",
          fixedTonicMidi: 60,
          drillMode: "graded",
          fsrsEnabled: false,
        },
        seed,
      );
      const invOptions: MixOption[] = chord.answerInversions.map((i) => ({
        id: String(i),
        label: INVERSION_LABELS[i],
        sublabel: INVERSION_CHORD_POOL[chord.chordType].shortSymbol,
      }));
      return {
        drillType: "inversion",
        audioPlan: chord.audioPlan,
        teachAudio: chord.teachAudio,
        options: invOptions,
        correctId: String(chord.inversionIndex),
        typeLabel: `Inversion (${INVERSION_CHORD_POOL[chord.chordType].longName})`,
        itemId: inversionDrillItemId(chord.chordType, chord.inversionIndex),
      };
    }

    case "cadence": {
      const round = buildCadenceExerciseRound(
        {
          cadences: ["PAC", "IAC", "plagal", "half", "deceptive"],
          contextLength: "full",
          tempo: "medium",
          tonic: "random",
          fixedTonicMidi: 60,
          drillMode: "graded",
          fsrsEnabled: false,
        },
        seed,
      );
      return {
        drillType: "cadence",
        audioPlan: round.audioPlan,
        teachAudio: round.teachAudio,
        options: CADENCE_OPTIONS,
        correctId: round.cadenceType,
        typeLabel: "Cadence",
        itemId: cadenceDrillItemId(round.cadenceType, "full"),
      };
    }

    case "mode": {
      const round = buildModeExerciseRound(
        {
          modes: ["ionian", "dorian", "phrygian", "lydian", "mixolydian", "aeolian", "locrian"],
          presentation: "ascending",
          tonic: "random",
          fixedTonicMidi: 60,
          drillMode: "graded",
          fsrsEnabled: false,
        },
        seed,
      );
      return {
        drillType: "mode",
        audioPlan: round.audioPlan,
        teachAudio: round.teachAudio,
        options: MODE_OPTIONS,
        correctId: round.mode,
        typeLabel: "Mode",
        itemId: modeDrillItemId(round.mode),
      };
    }

    case "progression": {
      const round = buildProgressionExerciseRound(
        {
          progressions: ["I-IV-V-I", "I-V-vi-IV", "I-vi-IV-V", "ii-V-I", "I-IV-I-V"],
          tempo: "medium",
          tonic: "random",
          fixedTonicMidi: 60,
          drillMode: "graded",
          fsrsEnabled: false,
        },
        seed,
        cadenceOpts,
      );
      return {
        drillType: "progression",
        audioPlan: round.audioPlan,
        teachAudio: round.teachAudio,
        options: PROGRESSION_OPTIONS.filter((o) =>
          (["I-IV-V-I", "I-V-vi-IV", "I-vi-IV-V", "ii-V-I", "I-IV-I-V"] as ProgressionKey[]).includes(
            o.id as ProgressionKey,
          ),
        ),
        correctId: round.progressionKey,
        typeLabel: "Progression",
        itemId: progressionDrillItemId(round.progressionKey),
      };
    }
  }
}

function generateItems(
  types: DrillType[],
  count: number,
  baseSeed: number,
  cadenceTempoKey: keyof typeof CADENCE_TEMPO_OPTS,
): MixItem[] {
  const items: MixItem[] = [];
  for (let i = 0; i < count; i++) {
    const type = types[i % types.length]!;
    items.push(buildMixItem(type, baseSeed + i * 7919, cadenceTempoKey));
  }
  return items;
}

// ─── State machine ────────────────────────────────────────────────────────────

type MixPhase = "loading" | "playing" | "awaiting" | "graded" | "done";

interface MixState {
  phase: MixPhase;
  items: MixItem[];
  itemIndex: number;
  selectedId: string | null;
  wasCorrect: boolean;
  wasTimeout: boolean;
  correctCount: number;
}

type MixAction =
  | { type: "LOADED"; items: MixItem[] }
  | { type: "PLAYBACK_DONE" }
  | { type: "SELECT"; id: string }
  | { type: "TIMEOUT" }
  | { type: "NEXT" }
  | { type: "DONE" };

function mixReducer(state: MixState, action: MixAction): MixState {
  switch (action.type) {
    case "LOADED":
      return { ...state, phase: "playing", items: action.items, itemIndex: 0 };

    case "PLAYBACK_DONE":
      if (state.phase !== "playing") return state;
      return { ...state, phase: "awaiting" };

    case "SELECT": {
      if (state.phase !== "awaiting") return state;
      const item = state.items[state.itemIndex];
      if (!item) return state;
      const wasCorrect = action.id === item.correctId;
      return {
        ...state,
        phase: "graded",
        selectedId: action.id,
        wasCorrect,
        wasTimeout: false,
        correctCount: state.correctCount + (wasCorrect ? 1 : 0),
      };
    }

    case "TIMEOUT": {
      if (state.phase !== "awaiting") return state;
      return {
        ...state,
        phase: "graded",
        selectedId: null,
        wasCorrect: false,
        wasTimeout: true,
      };
    }

    case "NEXT": {
      if (state.phase !== "graded") return state;
      const nextIndex = state.itemIndex + 1;
      if (nextIndex >= state.items.length) {
        return { ...state, phase: "done" };
      }
      return {
        ...state,
        phase: "playing",
        itemIndex: nextIndex,
        selectedId: null,
        wasCorrect: false,
        wasTimeout: false,
      };
    }

    case "DONE":
      return { ...state, phase: "done" };

    default:
      return state;
  }
}

const INITIAL_MIX_STATE: MixState = {
  phase: "loading",
  items: [],
  itemIndex: 0,
  selectedId: null,
  wasCorrect: false,
  wasTimeout: false,
  correctCount: 0,
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const SESSION_LENGTH = 20;

export function ExerciseMixPage(): ReactNode {
  const { mixId } = useParams<{ mixId: string }>();
  const mix = MIX_DEFS[mixId as MixId];

  const [state, dispatch] = useReducer(mixReducer, INITIAL_MIX_STATE);
  const playerRef = useRef<AudioPlayer | null>(null);

  const challengeStartedAtRef = useRef<string>(new Date().toISOString());
  const challengeResponseTimesRef = useRef<number[]>([]);
  const challengeResponseStartRef = useRef<number | null>(null);
  const previousBestRef = useRef<number | null | undefined>(undefined);

  const applyReview = useCardsStore((s) => s.applyReview);
  const instrument = usePrefsStore((s) => s.instrument);
  const cadenceTempo = usePrefsStore((s) => s.cadenceTempo);

  useEffect(() => {
    const player = createAudioPlayer();
    playerRef.current = player;
    return () => { player.stop(); player.destroy(); };
  }, []);

  useEffect(() => {
    const player = playerRef.current;
    if (!player || !instrument) return;
    void (async () => {
      await player.init();
      if (!player.isInstrumentLoaded(instrument)) {
        await player.loadInstrument(instrument);
      }
      player.setInstrument(instrument);
    })();
  }, [instrument]);

  // Generate items + load previous best on mount
  useEffect(() => {
    if (!mix) return;
    const items = generateItems(mix.types, SESSION_LENGTH, nextRoundSeed(), cadenceTempo);
    dispatch({ type: "LOADED", items });
    void getChallengeData(mix.challengeKey).then((d) => {
      previousBestRef.current = d.best;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const playCurrentItem = useCallback(
    async (item: MixItem) => {
      const player = playerRef.current;
      if (!player) return;
      await player.init();
      await player.play(item.audioPlan);
      dispatch({ type: "PLAYBACK_DONE" });
    },
    [],
  );

  // Play when entering playing phase
  useEffect(() => {
    if (state.phase !== "playing") return;
    const item = state.items[state.itemIndex];
    if (!item) return;
    void playCurrentItem(item);
  }, [state.phase, state.itemIndex, state.items, playCurrentItem]);

  // Record response start time
  useEffect(() => {
    if (state.phase !== "awaiting") return;
    challengeResponseStartRef.current = Date.now();
  }, [state.phase]);

  // Accumulate response time + write review + play teach audio on graded
  useEffect(() => {
    if (state.phase !== "graded") return;
    const responseMs = challengeResponseStartRef.current
      ? Date.now() - challengeResponseStartRef.current
      : 8000;
    challengeResponseTimesRef.current.push(responseMs);

    const item = state.items[state.itemIndex];
    if (item) {
      const correct = state.wasCorrect;
      const rating: Rating = correct ? (responseMs < 5000 ? "good" : "hard") : "again";
      void dbRecordReview({
        itemId: item.itemId,
        sessionId: "",
        rating: correct ? "good" : "again",
        correct,
        responseMs,
        chosenAnswerId: !correct ? state.selectedId : null,
        source: "challenge",
      });
      void applyReview(item.itemId, rating);

      if (!correct && item.teachAudio) {
        const player = playerRef.current;
        if (player) window.setTimeout(() => { void player.play(item.teachAudio!); }, 600);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase]);

  // Record challenge attempt on done
  useEffect(() => {
    if (state.phase !== "done" || !mix) return;
    const correct = state.correctCount;
    const times = challengeResponseTimesRef.current;
    const avgMs = times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;
    void recordChallengeAttempt({
      challengeKey: mix.challengeKey,
      startedAt: challengeStartedAtRef.current,
      items: SESSION_LENGTH,
      correct,
      accuracy: SESSION_LENGTH > 0 ? correct / SESSION_LENGTH : 0,
      averageResponseMs: Math.round(avgMs),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase]);

  // Auto-advance after timeout (600ms)
  useEffect(() => {
    if (!state.wasTimeout) return;
    const t = window.setTimeout(() => dispatch({ type: "NEXT" }), 600);
    return () => window.clearTimeout(t);
  }, [state.wasTimeout]);

  const handleSelect = useCallback((id: string) => {
    dispatch({ type: "SELECT", id });
  }, []);

  const handleTimeout = useCallback(() => {
    dispatch({ type: "TIMEOUT" });
  }, []);

  const { remainingMs } = useChallengeTimer({
    active: state.phase === "awaiting",
    timeMs: 8000,
    onTimeout: handleTimeout,
  });

  if (!mix) {
    return (
      <div className="max-w-xl mx-auto pt-8">
        <p className="font-serif italic text-inkSoft">Unknown mix: {mixId}</p>
        <Link to="/exercise" className="font-mono text-xs text-accent mt-4 inline-block">
          ← All drills
        </Link>
      </div>
    );
  }

  if (state.phase === "loading") {
    return (
      <div className="max-w-xl mx-auto pt-8">
        <p className="font-mono text-xs uppercase tracking-widest text-inkFaded">
          Building challenge…
        </p>
      </div>
    );
  }

  if (state.phase === "done") {
    const times = challengeResponseTimesRef.current;
    const avgMs = times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;
    return (
      <ChallengeScoreCard
        items={SESSION_LENGTH}
        correct={state.correctCount}
        averageResponseMs={avgMs}
        previousBest={previousBestRef.current ?? null}
        onAgain={() => {
          const items = generateItems(mix.types, SESSION_LENGTH, nextRoundSeed(), cadenceTempo);
          challengeStartedAtRef.current = new Date().toISOString();
          challengeResponseTimesRef.current = [];
          challengeResponseStartRef.current = null;
          dispatch({ type: "LOADED", items });
        }}
        onStop={() => window.history.back()}
      />
    );
  }

  const item = state.items[state.itemIndex];
  if (!item) return null;

  const isPlaying  = state.phase === "playing";
  const isAwaiting = state.phase === "awaiting";
  const isGraded   = state.phase === "graded";
  const isLastItem = state.itemIndex >= SESSION_LENGTH - 1;

  return (
    <div className="max-w-xl mx-auto pt-8 pb-16">
      <div className="flex items-center gap-3 mb-4">
        <Link
          to="/exercise"
          className="font-mono text-[10px] uppercase tracking-widest text-inkFaded hover:text-ink"
        >
          ← Ear Training
        </Link>
        <span className="font-mono text-[10px] text-inkFaded/40">·</span>
        <span className="font-mono text-[10px] uppercase tracking-widest text-inkFaded">
          {mix.title}
        </span>
      </div>

      {isAwaiting && (
        <ChallengeTimer remainingMs={remainingMs} totalMs={8000} />
      )}

      <div className="flex items-baseline justify-between mb-1">
        <p className="font-mono text-xs uppercase tracking-widest text-accent">challenge</p>
        <p className="font-mono text-xs text-inkFaded tabular-nums">
          {state.itemIndex + 1} of {SESSION_LENGTH}
        </p>
      </div>
      <h1 className="font-serif text-3xl font-medium text-ink mb-1">{mix.title}</h1>
      <p className="font-serif italic text-sm text-inkSoft mb-4">{mix.subtitle}</p>

      <div
        className="w-full h-0.5 bg-ink/10 mb-6"
        role="progressbar"
        aria-valuenow={state.itemIndex + 1}
        aria-valuemin={1}
        aria-valuemax={SESSION_LENGTH}
      >
        <div
          className="h-full bg-accent transition-all duration-300"
          style={{ width: `${((state.itemIndex + 1) / SESSION_LENGTH) * 100}%` }}
        />
      </div>

      {/* Exercise type label */}
      <p className="font-mono text-[10px] uppercase tracking-widest text-inkFaded mb-3">
        {item.typeLabel}
      </p>

      {/* Prompt */}
      <div className="mb-4 min-h-[2.5rem] flex items-center">
        {isPlaying && (
          <p className="font-serif italic text-xl text-inkSoft" aria-live="polite">
            Listen carefully…
          </p>
        )}
        {isAwaiting && (
          <p className="font-serif italic text-xl text-ink" aria-live="polite">
            Identify what you heard.
          </p>
        )}
        {isGraded && (
          <p className="font-serif italic text-ink" aria-live="polite">
            {state.wasTimeout
              ? `Time! The answer was ${item.options.find((o) => o.id === item.correctId)?.label ?? item.correctId}.`
              : state.wasCorrect
                ? "Correct."
                : `The answer was ${item.options.find((o) => o.id === item.correctId)?.label ?? item.correctId}.`}
          </p>
        )}
      </div>

      {/* Options */}
      {(isAwaiting || isGraded) && (
        <div className="flex flex-wrap gap-2 mb-6" role="group" aria-label="Answer options">
          {item.options.map((opt) => {
            const isSelected = state.selectedId === opt.id;
            const isCorrect = opt.id === item.correctId;
            let cls =
              "px-4 py-2 border text-sm transition-colors flex flex-col items-center min-w-[5rem]";
            if (isGraded) {
              if (isCorrect) {
                cls += " bg-accent/15 border-accent text-accent";
              } else if (isSelected && !isCorrect) {
                cls += " bg-red-500/10 border-red-400 text-red-400";
              } else {
                cls += " bg-paper border-ink/10 text-inkFaded";
              }
            } else {
              cls += " bg-paper border-ink/20 text-ink hover:border-ink hover:bg-ink/5 cursor-pointer";
            }
            return (
              <button
                key={opt.id}
                type="button"
                disabled={isGraded}
                onClick={() => handleSelect(opt.id)}
                className={cls}
                aria-pressed={isSelected}
              >
                <span>{opt.label}</span>
                {opt.sublabel && (
                  <span className="font-mono text-[10px] text-inkFaded mt-0.5">{opt.sublabel}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Next button */}
      {isGraded && !state.wasTimeout && (
        <button
          type="button"
          onClick={() => dispatch({ type: "NEXT" })}
          className="px-6 py-2.5 bg-ink text-paper hover:bg-accent transition-colors font-medium text-sm"
        >
          {isLastItem ? "Finish →" : "Next →"}
        </button>
      )}
    </div>
  );
}
